import { AppData, Lightning } from '@lightningjs/sdk';
import Placeholder from 'components/common/Placeholder';
import { SelectItemContext } from 'types/events';
import VisibilityIndicator from 'components/common/VisibilityIndicator';
import {
  updateContentHubThumbnailUrl,
  updateImageSize,
} from 'support/cwImageUtils';
import {
  HoverableListItem,
  HoverableListItemTemplateSpec,
} from 'components/common/HoverableListItem';
import { HoverableComponentTypeConfig } from 'components/common/HoverableComponent';
import { ContentHub } from 'services/cwData';
import { Media } from 'types/api/media';
import { SpeechType } from '@lightningjs/ui-components';
import { isContentHub } from 'support/contentUtils';
import Highlight from 'components/common/Highlight';
import { constants } from 'aliases';

export interface TileTemplateSpec extends HoverableListItemTemplateSpec {
  action: SelectItemContext['action'];
  data: Media | ContentHub | undefined;
  imageUrl: string;
  notifyWhenFullyVisible: boolean;
  parentContext: unknown;
  hoverHighlightEnabled: boolean;

  HoverHighlight: typeof Highlight;
  Placeholder: typeof Placeholder;
  Image: Lightning.textures.ImageTexture;
  VisibilityIndicator: typeof VisibilityIndicator;
}

export type CustomTileSpec = {
  type: Lightning.Component.Constructor;
  isCustomTile: true;
  w: number;
  h: number;
  labelText: string;
  action: string;
  actionArgs: string;
  parentContext: TileParentContext;
};

export type TileParentContext = {
  crossIndex: number;
  mainIndex: number;
  group?: number;
  rowTitle: string;
  isTivoSource: boolean;
  isContinueSource?: boolean;
};

const HIGHLIGHT_SIZE_OFFSET = constants.ui.highlightSizeOffset;
const HIGHLIGHT_POSITION_OFFSET = constants.ui.highlightPositionOffset;

export default abstract class Tile<
  TemplateSpec extends TileTemplateSpec = TileTemplateSpec,
  TypeConfig extends HoverableComponentTypeConfig = HoverableComponentTypeConfig,
> extends HoverableListItem<TemplateSpec, TypeConfig> {
  protected _Image = (this as Tile).getByRef('Image')!;
  protected _Placeholder = (this as Tile).getByRef('Placeholder')!;
  protected _VisibilityIndicator = (this as Tile).getByRef(
    'VisibilityIndicator',
  )!;
  protected _HoverHighlight = (this as Tile).getByRef('HoverHighlight')!;

  protected _data: TemplateSpec['data'] = undefined;
  protected _imageUrl: TemplateSpec['imageUrl'] = '';
  protected _action: TemplateSpec['action'] = undefined;
  private _notifyWhenFullyVisible = false;

  // Internal properties
  protected _hasImageLoaded = false;
  protected _isFullyVisible = false;
  protected _hasSignaledVisibility = false;
  private _hoverHighlightEnabled = false;

  /*
  used for storing data from the parent such tile index
  type is set to unknown to ensure no coupling between tile and parent (we should never have to know/use the type in the tile)
  */
  parentContext: unknown | null = null;

  protected showDecoration(enabled: boolean) {
    // Override in classes if needed
  }

  get title(): SpeechType {
    return this._data?.title ?? '';
  }

  get data() {
    return this._data;
  }

  set data(data: TemplateSpec['data']) {
    this._data = data;

    this.updateVisibilityIndicator();
    this.updateImage();
    this.updateAction();
  }

  get action(): TemplateSpec['action'] {
    return this._action;
  }

  set imageUrl(imageUrl: TemplateSpec['imageUrl']) {
    this._imageUrl = imageUrl;
    this.updateImage();
  }

  get notifyWhenFullyVisible() {
    return this._notifyWhenFullyVisible;
  }

  set notifyWhenFullyVisible(notifyWhenFullyVisible: boolean) {
    this._notifyWhenFullyVisible = notifyWhenFullyVisible;
    this.updateVisibilityIndicator();
  }

  // static getters required for collection wrapper (List, Carousel, etc.)
  static get width() {
    return 0;
  }

  static get height() {
    return 0;
  }

  static get highlightWidth() {
    return 0;
  }

  static get highlightHeight() {
    return 0;
  }

  static get expandedWidth(): number | null {
    return null;
  }

  static get expandedHeight(): number | null {
    return null;
  }

  get hoverHighlightEnabled(): boolean {
    return this._hoverHighlightEnabled;
  }

  set hoverHighlightEnabled(value: boolean) {
    this._hoverHighlightEnabled = value;
  }

  static override _template(): Lightning.Component.Template<TileTemplateSpec> {
    return {
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: 10,
      },
      HoverHighlight: {
        type: Highlight,
        visible: false,
        x: HIGHLIGHT_POSITION_OFFSET,
        y: HIGHLIGHT_POSITION_OFFSET,
        w: this.highlightWidth + HIGHLIGHT_SIZE_OFFSET,
        h: this.highlightHeight + HIGHLIGHT_SIZE_OFFSET,
      },
      Placeholder: {
        type: Placeholder,
        visible: true,
        w: this.highlightWidth,
        h: this.highlightHeight,
      },
      Image: {
        rect: true,
        w: this.highlightWidth,
        h: this.highlightHeight,
      },
      VisibilityIndicator: {
        type: VisibilityIndicator,
        w: this.highlightWidth,
        h: this.highlightHeight,
        signals: { indicateFullyVisible: '$indicateFullyVisible' },
        visible: false,
      },
    };
  }

  override _setup() {
    super._setup();
    this._isFullyVisible = false;
    this._hasSignaledVisibility = false;

    this.onLoadVisibilitySignals();
  }

  override _inactive() {
    super._inactive();
    this.clearListeners();
  }

  override _handleClick() {
    if (!AppData?.device.canMouseClick()) return;
    if (!this.hovered) return;

    this.onTileSelected();
  }

  override _handleEnter() {
    this.onTileSelected();
  }

  protected onTileSelected() {
    this.fireAncestors('$onTileSelected', this._data, {
      action: this.action,
      imageUrl: this._Image.src,
      parentContext: this.parentContext,
    });
  }

  private $indicateFullyVisible() {
    this._isFullyVisible = true;
    this.handleVisibilitySignal();
  }

  private updateVisibilityIndicator() {
    this._VisibilityIndicator.patch({
      visible: this._notifyWhenFullyVisible && !!this._data,
    });
  }

  private updateAction() {
    if (!this._data) return;
    this._action = isContentHub(this._data) ? 'navigation' : 'details';
  }

  protected updateImage() {
    // Only update image when we have data and image URL
    if (!this._data || !this._imageUrl) return;

    const src = isContentHub(this._data)
      ? updateContentHubThumbnailUrl(this._imageUrl, this._Image.w)
      : updateImageSize(this._imageUrl, this._Image.w);
    this._Image.patch({ src });
  }

  protected onLoadVisibilitySignals() {
    this._hasImageLoaded = false;

    this._Image.on('txLoaded', () => {
      this._Placeholder.patch({ visible: false });
      this._hasImageLoaded = true;

      this.handleVisibilitySignal();
    });

    this._Image.on('txUnloaded', () => {
      this._Placeholder.patch({ visible: true });
      this._hasImageLoaded = false;
    });
  }

  protected handleVisibilitySignal() {
    if (
      this._notifyWhenFullyVisible &&
      !this._hasSignaledVisibility &&
      this._isFullyVisible &&
      this._hasImageLoaded &&
      this._data
    ) {
      this.fireAncestors(
        '$onTileFullyVisible',
        this.parentContext,
        this._data,
        this._Image.src ?? '',
      );

      this._hasSignaledVisibility = true;
    }
  }

  override _handleHover(target: any) {
    if (this.hoverHighlightEnabled) this.setHoverHighlightVisible(true);
    if (this.isHoverable) this.toggleGlobalHoverListener(true);

    return super._handleHover(target);
  }

  override _handleUnhover(target: any) {
    if (this.hoverHighlightEnabled) this.setHoverHighlightVisible(false);
    if (this.isHoverable) this.toggleGlobalHoverListener(false);

    return super._handleUnhover(target);
  }

  private toggleGlobalHoverListener(enable: boolean) {
    this.baseToggleGlobalHoverListener(
      enable,
      this.onGlobalHoverStateChanged.bind(this),
      'Tile',
    );
  }

  protected baseToggleGlobalHoverListener(
    enable: boolean,
    callback: (arg: boolean) => void,
    uniqueKey = '',
  ) {
    const id = uniqueKey + this.id.toString();
    if (enable) {
      AppData?.device.addHoverStateChangedAction(id, callback);
    } else {
      AppData?.device.removeHoverStateChangedAction(id);
    }
  }

  onGlobalHoverStateChanged(isHovered: boolean) {
    if (isHovered) return;

    if (this.hoverHighlightEnabled) this.setHoverHighlightVisible(false);

    this.toggleGlobalHoverListener(false);
  }

  setHoverHighlightVisible(visible: boolean) {
    this._HoverHighlight.patch({ visible });
  }

  /**
   * Clears all txLoaded/txUnloaded listeners on the Image component, including
   * those created by components that extend the Tile class
   */
  protected clearListeners() {
    this._Image.removeAllListeners('txLoaded');
    this._Image.removeAllListeners('txUnloaded');
    this.toggleGlobalHoverListener(false);
  }
}
